import io from 'socket.io-client';
import logger from '../utils/logger';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.currentConversationId = null;
    this.messageHandlers = [];
    this.connectionHandlers = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.heartbeatInterval = null;
    this.reconnectCheckInterval = null;
    this.lastHeartbeat = null;
    this.connectionCheckInterval = 30000; // Check every 30 seconds
    this.heartbeatTimeout = 10000; // 10 seconds timeout for heartbeat
  }

  connect(serverUrl) {
    if (this.socket && this.socket.connected) {
      logger.log('Socket already connected');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      // Ensure serverUrl ends with /socket.io/ for Socket.IO connection
      const socketUrl = serverUrl.endsWith('/') ? serverUrl : serverUrl + '/';
      console.log('Connecting to Socket.IO server:', socketUrl);
      
      this.socket = io(socketUrl, {
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        maxReconnectionAttempts: this.maxReconnectAttempts,
        timeout: 20000,
        forceNew: true,
        path: '/socket.io/'  // Explicitly set Socket.IO path
      });

      this.socket.on('connect', () => {
        console.log('Socket connected:', this.socket.id);
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.lastHeartbeat = Date.now();
        
        // Start heartbeat mechanism
        this.startHeartbeat();
        
        // Start connection monitoring
        this.startConnectionMonitoring();
        
        // Rejoin conversation if was in one
        if (this.currentConversationId) {
          this.joinConversation(this.currentConversationId);
        }
        
        // Notify all connection handlers
        this.connectionHandlers.forEach(handler => {
          handler('connected');
        });
        
        resolve();
      });

      this.socket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
        this.isConnected = false;
        
        // Stop heartbeat and monitoring
        this.stopHeartbeat();
        this.stopConnectionMonitoring();
        
        // Notify all connection handlers
        this.connectionHandlers.forEach(handler => {
          handler('disconnected', reason);
        });
      });

      this.socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        this.reconnectAttempts++;
        
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('Max reconnection attempts reached');
          reject(error);
        }
      });

      // Listen for new messages
      this.socket.on('new-message', (messageData) => {
        console.log('Received message via socket:', messageData);
        
        // Notify all message handlers
        this.messageHandlers.forEach(handler => {
          handler(messageData);
        });
      });

      // Listen for user messages from other clients
      this.socket.on('user-message', (messageData) => {
        console.log('Received user message from another client:', messageData);
        
        // Notify all message handlers (same handlers as new-message for now)
        this.messageHandlers.forEach(handler => {
          handler({...messageData, isUserMessage: true, source: 'other_client'});
        });
      });

      // Set connection timeout
      setTimeout(() => {
        if (!this.isConnected) {
          reject(new Error('Socket connection timeout'));
        }
      }, 20000);
    });
  }

  disconnect() {
    if (this.socket) {
      console.log('Disconnecting socket...');
      if (this.currentConversationId) {
        this.leaveConversation(this.currentConversationId);
      }
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentConversationId = null;
    }
  }

  joinConversation(conversationId) {
    if (!this.socket || !this.isConnected) {
      console.warn('Socket not connected, cannot join conversation');
      return;
    }

    if (this.currentConversationId === conversationId) {
      console.log('Already in conversation:', conversationId);
      return;
    }

    // Leave current conversation if in one
    if (this.currentConversationId) {
      this.leaveConversation(this.currentConversationId);
    }

    console.log('Joining conversation:', conversationId);
    this.socket.emit('join-conversation', conversationId);
    this.currentConversationId = conversationId;
  }

  leaveConversation(conversationId) {
    if (!this.socket || !conversationId) {
      return;
    }

    console.log('Leaving conversation:', conversationId);
    this.socket.emit('leave-conversation', conversationId);
    
    if (this.currentConversationId === conversationId) {
      this.currentConversationId = null;
    }
  }

  broadcastUserMessage(messageData) {
    if (!this.socket || !this.isConnected || !this.currentConversationId) {
      console.warn('Cannot broadcast message: socket not connected or no conversation');
      return;
    }

    console.log('Broadcasting user message to other clients:', messageData);
    this.socket.emit('broadcast-user-message', {
      conversationId: this.currentConversationId,
      messageData: {
        ...messageData,
        socketId: this.socket.id,
        broadcastTime: new Date().toISOString()
      }
    });
  }

  onMessage(handler) {
    if (typeof handler !== 'function') {
      console.error('Message handler must be a function');
      return;
    }
    
    this.messageHandlers.push(handler);
    
    // Return unsubscribe function
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }

  onConnectionChange(handler) {
    if (typeof handler !== 'function') {
      console.error('Connection handler must be a function');
      return;
    }
    
    this.connectionHandlers.push(handler);
    
    // Return unsubscribe function
    return () => {
      this.connectionHandlers = this.connectionHandlers.filter(h => h !== handler);
    };
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      conversationId: this.currentConversationId,
      socketId: this.socket?.id || null
    };
  }

  isSocketConnected() {
    return this.isConnected && this.socket && this.socket.connected;
  }

  // Heartbeat mechanism to keep connection alive
  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing heartbeat
    
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        console.log('📡 Sending heartbeat ping...');
        this.socket.emit('ping', { timestamp: Date.now() });
        this.lastHeartbeat = Date.now();
      }
    }, 30000); // Send heartbeat every 30 seconds
    
    // Listen for pong response
    if (this.socket) {
      this.socket.on('pong', (data) => {
        console.log('💓 Received heartbeat pong:', data);
        this.lastHeartbeat = Date.now();
      });
    }
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Connection monitoring to detect stale connections
  startConnectionMonitoring() {
    this.stopConnectionMonitoring(); // Clear any existing monitoring
    
    this.reconnectCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastHeartbeat = now - (this.lastHeartbeat || now);
      
      console.log('🔍 Connection check:', {
        connected: this.isConnected,
        socketConnected: this.socket?.connected,
        timeSinceLastHeartbeat: timeSinceLastHeartbeat,
        heartbeatTimeout: this.heartbeatTimeout
      });
      
      // If no heartbeat response for too long, force reconnect
      if (this.isConnected && timeSinceLastHeartbeat > this.heartbeatTimeout * 6) { // 60 seconds
        console.warn('🚨 Stale connection detected, forcing reconnect...');
        this.forceReconnect();
      }
      
      // If socket says connected but we think it's not, sync state
      if (this.socket && !this.socket.connected && this.isConnected) {
        console.warn('🔄 Socket state mismatch detected, updating state...');
        this.isConnected = false;
        this.connectionHandlers.forEach(handler => {
          handler('disconnected', 'state_mismatch');
        });
      }
    }, this.connectionCheckInterval);
  }

  stopConnectionMonitoring() {
    if (this.reconnectCheckInterval) {
      clearInterval(this.reconnectCheckInterval);
      this.reconnectCheckInterval = null;
    }
  }

  // Force reconnect for stale connections
  forceReconnect() {
    console.log('🔄 Forcing socket reconnection...');
    
    if (this.socket) {
      this.socket.disconnect();
      this.stopHeartbeat();
      this.stopConnectionMonitoring();
    }
    
    // Wait a bit then reconnect
    setTimeout(() => {
      if (this.socket) {
        console.log('🔌 Attempting to reconnect...');
        this.socket.connect();
      }
    }, 2000);
  }

  // Manual reconnect method for user-triggered reconnection
  reconnect() {
    console.log('🔄 Manual reconnection requested...');
    return this.forceReconnect();
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
